import { useEffect, useState, useCallback } from 'react';
import { ensureAuth, isAuthDisabled, getToken, getTokenExpiry, redirectToLogin, isDevLogin, redirectToSystemAdmin, SYSTEM_ADMIN_ROUTE } from './auth/auth';
import { setMyPermissions, getMyPermissions as getCachedPermissions, canAdmin, canSystemAdmin, canView } from './auth/permissions';
import { getMyPermissions as fetchMyPermissions } from './features/dashboard/api/dashboardApi';
import LandingPage from './pages/LandingPage.jsx';
import LocalUserPicker, { getLocalUser, clearLocalUser } from './pages/LocalUserPicker.jsx';
import DashboardRegionStatusPage from './features/dashboard/pages/DashboardRegionStatusPage.jsx';
import DashboardProgressManagementPage from './features/dashboard/pages/DashboardProgressManagementPage.jsx';
import DashboardProgressReportPage from './features/dashboard/pages/DashboardProgressReportPage.jsx';
import DashboardRiskManagementPage from './features/dashboard/pages/DashboardRiskManagementPage.jsx';
import DashboardProblemManagementPage from './features/dashboard/pages/DashboardProblemManagementPage.jsx';
import DashboardDeliverableManagementPage from './features/dashboard/pages/DashboardDeliverableManagementPage.jsx';
import DashboardAiManagementPage from './features/dashboard/pages/DashboardAiManagementPage.jsx';
import DashboardRegionProgressReportPage from './features/dashboard/pages/DashboardRegionProgressReportPage.jsx';
import DashboardMasterPage from './features/dashboard/pages/DashboardMasterPage.jsx';
import LifeHomePage from './features/life/pages/LifeHomePage.jsx';
import AwsBillingMasterPage from './features/aws-billing/pages/AwsBillingMasterPage.jsx';
import DashboardScheduleListPage from './features/dashboard/pages/DashboardScheduleListPage.jsx';
import DashboardRegionRiskPage from './features/dashboard/pages/DashboardRegionRiskPage.jsx';
import DashboardProjectRiskPage from './features/dashboard/pages/DashboardProjectRiskPage.jsx';
import DashboardRiskStatsPage from './features/dashboard/pages/DashboardRiskStatsPage.jsx';
import SystemAdminPage from './features/system/pages/SystemAdminPage.jsx';
import SystemAdminLoginPage from './features/system/pages/SystemAdminLoginPage.jsx';

function getRouteFromLocation(/* istanbul ignore next -- 呼び出し元は常に window.location を渡す */ location = window.location) {
	/* istanbul ignore next -- ブラウザの pathname は必ず '/' 始まり */
	const pathname = location.pathname || '/';
	const hash = location.hash || '';

	if (hash.startsWith('#/')) {
		/* istanbul ignore next -- 直前で '#/' 始まりを確かめているため空にならない */
		const hashRoute = hash.slice(1) || '/';
		if (hashRoute === '/' && pathname !== '/') {
			return pathname;
		}
		return hashRoute;
	}

	return pathname;
}

function normalizeLegacyHashUrl() {
	const hash = window.location.hash || '';
	// Cognito コールバックのハッシュ (#access_token=...) を保護
	if (hash.includes('access_token=')) {
		return /* istanbul ignore next -- 同上 */ (window.location.pathname || '/');
	}

	const nextRoute = getRouteFromLocation(window.location);
	const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
	const nextUrl = `${nextRoute}${window.location.search}`;

	if (currentUrl !== nextUrl) {
		window.history.replaceState({}, '', nextUrl);
	}

	return nextRoute;
}

function AuthToast({ onClose }) {
	useEffect(() => {
		const timer = setTimeout(onClose, 3000);
		return () => clearTimeout(timer);
	}, [onClose]);

	return (
		<div style={{
			position: 'fixed',
			top: 24,
			right: 24,
			background: '#16a34a',
			color: '#fff',
			padding: '12px 24px',
			borderRadius: 8,
			boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
			zIndex: 9999,
			fontSize: 14,
			fontWeight: 500,
		}}>
			認証が完了しました
		</div>
	);
}

function App() {
	const [authStatus, setAuthStatus] = useState(() =>
		isAuthDisabled() ? 'authenticated' : 'pending'
	);
	const [showAuthToast, setShowAuthToast] = useState(false);
	const [route, setRoute] = useState(() => normalizeLegacyHashUrl());
	// 権限情報が読み込まれるまで「権限がない」表示を防ぐ
	const [permissionsReady, setPermissionsReady] = useState(() => {
		if (isAuthDisabled()) return true;
		return getCachedPermissions().length > 0;
	});
	// ローカル開発用: ユーザー選択済みかどうか
	// 先に権限キャッシュを読むことで、TTL 切れならユーザー選択も解除され、
	// 権限ゼロ（＝全画面が閲覧専用）のまま起動せずユーザー選択画面に戻る。
	const [localUserSelected, setLocalUserSelected] = useState(() => {
		if (!isAuthDisabled()) return true;
		getCachedPermissions();
		return Boolean(getLocalUser());
	});

	// 権限情報を API から取得して sessionStorage に保存
	const loadPermissions = useCallback(() => {
		/* istanbul ignore next -- loadPermissions は authStatus が 'pending' の時のみ呼ばれ、
		   それは isAuthDisabled()===false が前提。isAuthDisabled は env 定数のため到達不能な防御分岐。 */
		if (isAuthDisabled()) {
			setPermissionsReady(true);
			return;
		}
		fetchMyPermissions()
			.then((perms) => {
				setMyPermissions(perms);
				setPermissionsReady(true);
			})
			.catch((err) => {
				console.error('[App] 権限情報の取得に失敗しました:', err);
				setPermissionsReady(true);
			});
	}, []);

	// 開発者ログイン成功時: 権限は画面側で設定済みなので、認証済みにして '/' へ遷移
	const handleDevLoginSuccess = useCallback(() => {
		setPermissionsReady(true);
		window.history.replaceState({}, '', '/');
		setRoute('/');
		setAuthStatus('authenticated');
	}, []);

	useEffect(() => {
		if (authStatus === 'pending') {
			const result = ensureAuth();
			setAuthStatus(result);
			if (result === 'authenticated' || result === 'callback') {
				setRoute(normalizeLegacyHashUrl());
				if (result === 'callback') {
					setShowAuthToast(true);
				}
				loadPermissions();
			}
			return;
		}

		if (authStatus !== 'authenticated' && authStatus !== 'callback') {
			return;
		}

		setRoute(normalizeLegacyHashUrl());

		const handlePopState = () => setRoute(normalizeLegacyHashUrl());
		window.addEventListener('popstate', handlePopState);

		const handleClick = (e) => {
			const anchor = e.target.closest('a');
			if (!anchor) return;
			if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
			if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
			const href = anchor.getAttribute('href');
			if (!href || href.startsWith('#') || href.startsWith('http') || href.startsWith('//') || href.startsWith('mailto:')) return;
			e.preventDefault();
			window.history.pushState({}, '', href);
			setRoute(getRouteFromLocation(window.location));
		};
		document.addEventListener('click', handleClick);

		return () => {
			window.removeEventListener('popstate', handlePopState);
			document.removeEventListener('click', handleClick);
		};
	}, [authStatus, loadPermissions]);

	// トークン期限切れを定期チェック（60秒ごと）
	// 期限切れ検知時に Cognito ログインへ自動リダイレクト
	useEffect(() => {
		if (isAuthDisabled()) return;
		if (authStatus !== 'authenticated' && authStatus !== 'callback') return;

		const check = () => {
			if (!getToken()) {
				// 開発者ログイン中は MS ではなく開発者ログイン画面へ戻す
				if (isDevLogin()) {
					redirectToSystemAdmin();
				} else {
					redirectToLogin();
				}
			}
		};

		// 期限切れ直前にもチェックするタイマーを設定
		const expiry = getTokenExpiry();
		let expiryTimer;
		if (expiry > 0) {
			const msUntilExpiry = expiry - Date.now();
			if (msUntilExpiry > 0) {
				expiryTimer = setTimeout(check, msUntilExpiry + 1000);
			}
		}

		const interval = setInterval(check, 60_000);
		return () => {
			clearInterval(interval);
			if (expiryTimer) clearTimeout(expiryTimer);
		};
	}, [authStatus]);

	// /system-admin は意図的な入口。認証状態やSPA遷移に関わらず常にログイン画面を開く
	// （ローカルは認証無効のため対象外）
	if (authStatus === 'devlogin' || (!isAuthDisabled() && route === SYSTEM_ADMIN_ROUTE)) {
		return <SystemAdminLoginPage onSuccess={handleDevLoginSuccess} />;
	}

	// 認証失敗・リダイレクト中・未認証 → 何も表示しない
	if (authStatus !== 'authenticated' && authStatus !== 'callback') {
		return null;
	}

	// ローカル開発時: ユーザー未選択 → 選択画面を表示
	if (isAuthDisabled() && !localUserSelected) {
		return (
			<LocalUserPicker
				onSelect={() => setLocalUserSelected(true)}
			/>
		);
	}

	// 権限情報取得中 → ローディング表示
	if (!permissionsReady) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#6b7280', fontSize: 14 }}>
				読み込み中...
			</div>
		);
	}

	const accessDenied = <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 16 }}>このページへのアクセス権限がありません。</div>;
	const masterDenied = <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 16 }}>このページは管理者のみアクセスできます。</div>;

	const page = (() => {
		if (route.startsWith('/master')) {
			const hasAnyAdmin = canSystemAdmin() || canAdmin('ダッシュボード') || canAdmin('Life') || canAdmin('AWS利用料');
			return hasAnyAdmin ? <DashboardMasterPage /> : masterDenied;
		}
		if (route.startsWith('/system/master')) {
			return canSystemAdmin() ? <SystemAdminPage /> : masterDenied;
		}
		if (route.startsWith('/life')) {
			const accessDeniedLife = <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 16 }}>このページへのアクセス権限がありません。</div>;
			return canView('Life') ? <LifeHomePage onNavigate={(path) => { window.history.pushState({}, '', path); setRoute(path); }} /> : accessDeniedLife;
		}
		if (route.startsWith('/aws-billing/master')) {
			return canAdmin('AWS利用料') ? <AwsBillingMasterPage /> : masterDenied;
		}
		if (route.startsWith('/dashboard/master')) {
			return canAdmin('ダッシュボード') ? <DashboardMasterPage /> : masterDenied;
		}
		if (route.startsWith('/dashboard')) {
			if (!canView('ダッシュボード')) return accessDenied;
			if (route.startsWith('/dashboard/progress-report')) {
				return <DashboardProgressReportPage />;
			}
			if (route.startsWith('/dashboard/region-progress-report')) {
				return <DashboardRegionProgressReportPage />;
			}
			if (route.startsWith('/dashboard/schedule-list')) {
				return <DashboardScheduleListPage />;
			}
			if (route.startsWith('/dashboard/project-risk')) {
				return <DashboardProjectRiskPage />;
			}
			if (route.startsWith('/dashboard/risk-stats')) {
				return <DashboardRiskStatsPage />;
			}
			if (route.startsWith('/dashboard/region-risk')) {
				return <DashboardRegionRiskPage />;
			}
			if (route.startsWith('/dashboard/progress-management')) {
				return <DashboardProgressManagementPage />;
			}
			if (route.startsWith('/dashboard/risk-management')) {
				return <DashboardRiskManagementPage />;
			}
			if (route.startsWith('/dashboard/problem-management')) {
				return <DashboardProblemManagementPage />;
			}
			if (route.startsWith('/dashboard/deliverable-management')) {
				return <DashboardDeliverableManagementPage />;
			}
			if (route.startsWith('/dashboard/ai-management')) {
				return <DashboardAiManagementPage />;
			}
			return <DashboardRegionStatusPage />;
		}
		return <LandingPage />;
	})();

	return (
		<>
			{showAuthToast && (
				<AuthToast onClose={() => setShowAuthToast(false)} />
			)}
			{page}
			{isAuthDisabled() && localUserSelected && (
				<button
					onClick={() => {
						clearLocalUser();
						setLocalUserSelected(false);
					}}
					type="button"
					style={{
						position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
						padding: '6px 14px', fontSize: 11, fontWeight: 600,
						background: '#1f2937', color: '#fff', border: 'none',
						borderRadius: 6, cursor: 'pointer', opacity: 0.7,
					}}
					title="ユーザーを切り替え"
				>
					{sessionStorage.getItem('mobibi_user_name') || 'DEV'} - 切替
				</button>
			)}
		</>
	);
}

export default App;
